import { supabase, TABLES, isSupabaseConfigured } from '../config/supabase';
import { storeVoteInSupabase, loadUserVotes, loadCategoryVotes } from './surveyVotesService';
import { queuedSupabaseOperation } from '../utils/supabaseWrapper';

// Survey Categories
export const createSurveyCategory = async (categoryData, currentUser) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  if (!currentUser) {
    throw new Error('User not authenticated');
  }
  
  try {
    // Use the provided user information instead of Supabase auth
    const createdBy = currentUser.userId || currentUser.email || 'anonymous';

    const insertData = {
      name: categoryData.name,
      description: categoryData.description,
      movie_ids: categoryData.movieIds || [],
      header_image_url: categoryData.headerImageUrl || null,
      is_active: true,
      is_public: categoryData.isPublic !== undefined ? categoryData.isPublic : true,
      is_closed: false,
      is_archived: false,
      created_by: createdBy,
      display_order: 0 // Add display_order with default value
    };

    console.log('Creating survey category with data:', insertData);

    const data = await queuedSupabaseOperation.insert(
      'survey_categories',
      insertData,
      'surveyCategories', // localStorage fallback key
      `Create survey category: ${categoryData.name}`
    );

    console.log('Survey category created successfully:', data);
    return { data, error: null };
  } catch (err) {
    console.error('Error creating survey category:', err);
    console.error('Error details:', {
      message: err.message,
      stack: err.stack,
      name: err.name
    });
    return { data: null, error: err };
  }
};

export const getSurveyCategories = async (activeOnly = true) => {
  if (!isSupabaseConfigured()) {
    console.log('getSurveyCategories: Supabase not configured, returning empty array');
    return { data: [], error: null }; // Return empty data instead of throwing error
  }
  
  try {
    // Build query string for queue system (includes both selection and filters)
    let queryString = 'select=*&order=display_order.asc';
    if (activeOnly) {
      queryString += '&is_active=eq.true&is_archived=eq.false';
    }

    console.log('🔍 getSurveyCategories query debug:', { 
      activeOnly, 
      queryString, 
      fullUrl: `survey_categories?${queryString}` 
    });

    const rawData = await queuedSupabaseOperation.select(
      'survey_categories',
      queryString, // Complete query with select and filters
      null, // No localStorage fallback for select
      'Load survey categories'
    );

    console.log('🔍 Raw data from database:', rawData);
    
    // Normalize the data - ensure movie_ids is always an array
    const data = rawData?.map(category => {
      let normalizedMovieIds = [];
      
      if (Array.isArray(category.movie_ids)) {
        normalizedMovieIds = category.movie_ids;
      } else if (typeof category.movie_ids === 'string') {
        try {
          normalizedMovieIds = JSON.parse(category.movie_ids || '[]');
        } catch (parseError) {
          console.warn('Failed to parse movie_ids for category', category.name, ':', category.movie_ids);
          normalizedMovieIds = [];
        }
      } else {
        normalizedMovieIds = category.movie_ids || [];
      }
      
      return {
        ...category,
        movie_ids: normalizedMovieIds
      };
    }) || [];
    
    console.log('Data normalization debug:', {
      rawDataCount: rawData?.length || 0,
      normalizedDataCount: data?.length || 0,
      sampleRawMovieIds: rawData?.[0]?.movie_ids,
      sampleRawMovieIdsType: typeof rawData?.[0]?.movie_ids,
      sampleNormalizedMovieIds: data?.[0]?.movie_ids,
      sampleNormalizedMovieIdsType: typeof data?.[0]?.movie_ids,
      isArray: Array.isArray(data?.[0]?.movie_ids)
    });
    
    // Additional debug: Check total categories without is_active filter
    if (activeOnly) {
      try {
        const allData = await queuedSupabaseOperation.select(
          'survey_categories',
          'id,name,is_active',
          'order=created_at.desc',
          null,
          'Load all survey categories for debug'
        );
        
        console.log('All categories (including inactive):', {
          totalCount: allData?.length || 0,
          activeCount: allData?.filter(cat => cat.is_active).length || 0,
          inactiveCount: allData?.filter(cat => !cat.is_active).length || 0,
          allError: 'None'
        });
      } catch (debugError) {
        console.log('Debug query error:', debugError);
      }
    }
    
    // Debug logging
    console.log('getSurveyCategories Debug:', {
      activeOnly,
      dataCount: data?.length || 0,
      data: data?.map(cat => ({
        id: cat.id,
        name: cat.name,
        movie_ids: cat.movie_ids,
        movieCount: cat.movie_ids?.length || 0,
        is_active: cat.is_active
      })),
      error: null,
      errorMessage: 'None',
      errorDetails: 'None',
      errorHint: 'None',
      errorCode: 'None'
    });

    // Check if the deleted category is still in the results
    if (window.lastDeletedCategoryId) {
      const stillExists = data?.find(cat => cat.id === window.lastDeletedCategoryId);
      console.log('Checking if deleted category still exists:', {
        deletedId: window.lastDeletedCategoryId,
        stillExists: !!stillExists,
        categoryName: stillExists?.name
      });
    }
    
    return { data: data || [], error: null };
  } catch (err) {
    console.error('Error fetching survey categories:', err);
    return { data: null, error: err };
  }
};

export const updateSurveyCategory = async (categoryId, updates) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    // Normalize the field names for database
    const dbUpdates = { ...updates };
    if (updates.movieIds) {
      dbUpdates.movie_ids = updates.movieIds;
      delete dbUpdates.movieIds;
    }
    if (updates.headerImageUrl !== undefined) {
      dbUpdates.header_image_url = updates.headerImageUrl || null;
      delete dbUpdates.headerImageUrl;
    }

    console.log('Updating survey category:', categoryId, 'with data:', dbUpdates);

    const data = await queuedSupabaseOperation.update(
      'survey_categories',
      dbUpdates,
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Update survey category: ${categoryId}`
    );

    console.log('Update category result:', { data, rowsAffected: data?.length || 0 });
    
    if (data && data.length > 0) {
      return { data: data[0], error: null }; // Return single object like before
    } else {
      throw new Error('No rows were updated - category may not exist or no changes were made');
    }
  } catch (err) {
    console.error('Error updating survey category:', err);
    return { data: null, error: err };
  }
};

export const deleteSurveyCategory = async (categoryId, currentUser = null) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    console.log('Deleting category with ID:', categoryId);
    
    // Track the deleted category for debugging
    window.lastDeletedCategoryId = categoryId;
    
    // Use provided user information instead of Supabase auth
    console.log('Current user for delete:', currentUser ? { email: currentUser.email, name: currentUser.name } : 'No user');
    
    const deleteData = await queuedSupabaseOperation.delete(
      'survey_categories',
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No localStorage fallback for delete
      `Delete survey category: ${categoryId}`
    );

    console.log('Supabase delete response:', { 
      error: null, 
      deleteData, 
      count: deleteData?.length || 0, 
      deletedRows: deleteData?.length || 0 
    });
    
    return { error: null };
  } catch (err) {
    console.error('Error deleting survey category:', err);
    return { error: err };
  }
};

// Update category order
export const updateCategoryOrder = async (categoryUpdates) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    console.log('Updating category order:', categoryUpdates);
    
    // Update each category's display_order
    const updatePromises = categoryUpdates.map(({ id, display_order }) =>
      supabase
        .from(TABLES.SURVEY_CATEGORIES)
        .update({ display_order })
        .eq('id', id)
    );
    
    const results = await Promise.all(updatePromises);
    
    // Check for errors
    const errors = results.filter(result => result.error);
    if (errors.length > 0) {
      throw new Error(`Failed to update some categories: ${errors.map(e => e.error.message).join(', ')}`);
    }
    
    console.log('Category order updated successfully');
    return { error: null };
  } catch (err) {
    console.error('Error updating category order:', err);
    return { error: err };
  }
};

// Survey Votes
export const submitVote = async (voteData) => {
  try {
    const dbVoteData = {
      category_id: voteData.categoryId,
      movie_id: voteData.movieId,
      vote_value: voteData.voteValue,
      user_email: voteData.userEmail,
      updated_at: new Date().toISOString()
    };

    // Note: user_id column doesn't exist in current schema, using user_email only
    // TODO: Add user_id column to survey_votes table if needed in the future

    console.log('📊 Submitting vote:', {
      categoryId: voteData.categoryId,
      movieId: voteData.movieId,
      voteValue: voteData.voteValue,
      userEmail: voteData.userEmail,
      userId: voteData.userId
    });

    // Use the same pattern as saveMovieViewer - try update first, then insert
    try {
      // First try to update existing vote
      const updateResult = await queuedSupabaseOperation.update(
        'survey_votes',
        { 
          vote_value: voteData.voteValue,
          updated_at: new Date().toISOString()
        },
        `category_id=eq.${encodeURIComponent(voteData.categoryId)}&movie_id=eq.${encodeURIComponent(voteData.movieId)}&user_email=eq.${encodeURIComponent(voteData.userEmail)}`,
        null, // No localStorage fallback for update
        `Update vote: ${voteData.userEmail} -> ${voteData.movieId} = ${voteData.voteValue}`
      );
      
      // Check if any rows were actually updated
      if (updateResult && updateResult.length > 0) {
        console.log('✅ Vote updated in Supabase:', voteData.userEmail, voteData.movieId, voteData.voteValue);
        return { data: updateResult, error: null };
      } else {
        // Throw an error to trigger the insert logic
        throw new Error('No rows updated - vote record does not exist');
      }
    } catch (updateError) {
      // If update fails (record doesn't exist), try insert
      console.log('➕ Inserting new vote');
      
      const insertResult = await queuedSupabaseOperation.insert(
        'survey_votes',
        dbVoteData,
        `survey_vote_${voteData.userEmail}_${voteData.categoryId}_${voteData.movieId}`,
        `Insert vote: ${voteData.userEmail} -> ${voteData.movieId} = ${voteData.voteValue}`
      );
      
      console.log('✅ Vote inserted in Supabase:', voteData.userEmail, voteData.movieId, voteData.voteValue);
      return { data: insertResult, error: null };
    }
  } catch (err) {
    console.error('❌ Error submitting vote:', err);
    return { data: null, error: err };
  }
};

// Use Supabase storage with localStorage fallback
// eslint-disable-next-line no-unused-vars -- dead code, kept for now
const submitVoteLocally = async (voteData) => {
  // Use the new Supabase service which includes localStorage fallback
  return await storeVoteInSupabase(voteData);
};

export const getUserVotes = async (userInfo, categoryId = null) => {
  // Use the new Supabase service which includes localStorage fallback
  return await loadUserVotes(userInfo, categoryId);
};

// Local storage fallback for getting user votes
// eslint-disable-next-line no-unused-vars -- dead code, kept for now
const getUserVotesLocally = (userInfo, categoryId = null) => {
  try {
    const votes = JSON.parse(localStorage.getItem('survey_votes') || '{}');
    const userEmail = userInfo.email || userInfo.userEmail;
    
    const userVotes = Object.values(votes).filter(vote => {
      const matchesUser = vote.user_email === userEmail;
      const matchesCategory = categoryId ? vote.category_id === categoryId : true;
      return matchesUser && matchesCategory;
    });
    
    return { data: userVotes, error: null };
  } catch (err) {
    console.error('Error fetching user votes locally:', err);
    return { data: [], error: err };
  }
};

export const getCategoryVoteResults = async (categoryId) => {
  try {
    console.log('getCategoryVoteResults: Fetching votes for category:', categoryId);
    
    // Use the new Supabase service which includes localStorage fallback
    const votes = await loadCategoryVotes(categoryId);
    
    console.log('getCategoryVoteResults: Raw vote data:', { 
      categoryId, 
      votes,
      voteCount: votes?.length || 0
    });

    // Group votes by movie
    const votesByMovie = {};
    votes.forEach(vote => {
      if (!votesByMovie[vote.movieId]) {
        votesByMovie[vote.movieId] = {
          votes: [],
          totalVotes: 0,
          averageScore: 0,
          distribution: { '-1': 0, 0: 0, 1: 0, 2: 0, 3: 0 }
        };
      }
      
      votesByMovie[vote.movieId].votes.push(vote);
      votesByMovie[vote.movieId].totalVotes++;
      votesByMovie[vote.movieId].distribution[vote.voteValue]++;
    });

    // Calculate averages
    Object.keys(votesByMovie).forEach(movieId => {
      const movieVotes = votesByMovie[movieId];
      const totalScore = movieVotes.votes.reduce((sum, vote) => sum + vote.voteValue, 0);
      movieVotes.averageScore = movieVotes.totalVotes > 0 ? totalScore / movieVotes.totalVotes : 0;
    });

    console.log('getCategoryVoteResults: Final processed results:', {
      categoryId,
      movieCount: Object.keys(votesByMovie).length,
      movies: Object.keys(votesByMovie),
      results: votesByMovie
    });

    return { data: votesByMovie, error: null };
  } catch (err) {
    console.error('Error fetching vote results:', err);
    return { data: null, error: err };
  }
};

// User Profiles (optional - for storing additional user info)
export const createOrUpdateUserProfile = async (userId, profileData) => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_PROFILES)
      .upsert([{
        user_id: userId,
        email: profileData.email,
        display_name: profileData.displayName,
        avatar_url: profileData.avatarUrl,
        updated_at: new Date().toISOString()
      }], {
        onConflict: 'user_id'
      })
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (err) {
    console.error('Error creating/updating user profile:', err);
    return { data: null, error: err };
  }
};

export const getUserProfile = async (userId) => {
  try {
    const { data, error } = await supabase
      .from(TABLES.USER_PROFILES)
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw error;
    }
    
    return { data: data || null, error: null };
  } catch (err) {
    console.error('Error fetching user profile:', err);
    return { data: null, error: err };
  }
};

// Survey Management Functions
export const updateCategoryVisibility = async (categoryId, isPublic) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    const data = await queuedSupabaseOperation.update(
      'survey_categories',
      { is_public: isPublic },
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Update category visibility: ${categoryId}`
    );

    console.log('Update visibility result:', { data, rowsAffected: data?.length || 0 });
    return { data, error: null };
  } catch (err) {
    console.error('Error updating category visibility:', err);
    return { data: null, error: err };
  }
};

export const closeSurveyCategory = async (categoryId) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    const data = await queuedSupabaseOperation.update(
      'survey_categories',
      { 
        is_closed: true,
        closed_at: new Date().toISOString()
      },
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Close survey category: ${categoryId}`
    );

    console.log('Close survey result:', { data, rowsAffected: data?.length || 0 });
    return { data, error: null };
  } catch (err) {
    console.error('Error closing survey category:', err);
    return { data: null, error: err };
  }
};

export const reopenSurveyCategory = async (categoryId) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    const data = await queuedSupabaseOperation.update(
      'survey_categories',
      { 
        is_closed: false,
        closed_at: null
      },
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Reopen survey category: ${categoryId}`
    );

    console.log('Reopen survey result:', { data, rowsAffected: data?.length || 0 });
    return { data, error: null };
  } catch (err) {
    console.error('Error reopening survey category:', err);
    return { data: null, error: err };
  }
};

export const archiveSurveyCategory = async (categoryId) => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase is not configured. Please set up your Supabase credentials in Settings.');
  }
  
  try {
    const data = await queuedSupabaseOperation.update(
      'survey_categories',
      { 
        is_active: false,    // 🔧 FIX: Set to inactive so it doesn't appear in Vote/Results
        is_archived: true,
        archived_at: new Date().toISOString()
      },
      `id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Archive survey category: ${categoryId}`
    );

    console.log('Archive survey result:', { data, rowsAffected: data?.length || 0 });
    return { data, error: null };
  } catch (err) {
    console.error('Error archiving survey category:', err);
    return { data: null, error: err };
  }
};

export const getArchivedSurveyCategories = async () => {
  if (!isSupabaseConfigured()) {
    console.log('getArchivedSurveyCategories: Supabase not configured');
    return { data: [], error: null };
  }
  
  try {
    console.log('getArchivedSurveyCategories: Fetching archived categories...');
    const rawData = await queuedSupabaseOperation.select(
      'survey_categories',
      'select=*&is_archived=eq.true&order=archived_at.desc', // Fixed parameter order
      null, // No localStorage fallback
      'Load archived survey categories'
    );

    // Normalize the data - ensure movie_ids is always an array (same as other functions)
    const data = rawData?.map(category => {
      let normalizedMovieIds = [];
      
      if (Array.isArray(category.movie_ids)) {
        normalizedMovieIds = category.movie_ids;
      } else if (typeof category.movie_ids === 'string') {
        try {
          normalizedMovieIds = JSON.parse(category.movie_ids || '[]');
        } catch (parseError) {
          console.warn('Failed to parse movie_ids for archived category', category.name, ':', category.movie_ids);
          normalizedMovieIds = [];
        }
      } else {
        normalizedMovieIds = category.movie_ids || [];
      }
      
      return {
        ...category,
        movie_ids: normalizedMovieIds
      };
    }) || [];

    console.log('getArchivedSurveyCategories response:', { 
      data, 
      dataCount: data?.length || 0,
      firstItem: data?.[0],
      firstItemMovieIds: data?.[0]?.movie_ids,
      firstItemMovieIdsType: typeof data?.[0]?.movie_ids
    });

    return { data, error: null };
  } catch (err) {
    console.error('Error fetching archived survey categories:', err);
    return { data: [], error: err };
  }
};

export const deleteArchivedSurveyCategory = async (categoryId) => {
  if (!isSupabaseConfigured()) {
    return { data: null, error: new Error('Supabase not configured') };
  }

  try {
    console.log('Deleting archived survey category:', categoryId);
    
    // First delete all votes for this category using queue system
    const votesDeleteResult = await queuedSupabaseOperation.delete(
      'survey_votes',
      `category_id=eq.${encodeURIComponent(categoryId)}`,
      null, // No fallback
      `Delete votes for archived category: ${categoryId}`
    );

    console.log('Votes deletion result:', { votesDeleteResult, count: votesDeleteResult?.length || 0 });

    // Then delete the category itself using queue system
    const categoryDeleteResult = await queuedSupabaseOperation.delete(
      'survey_categories',
      `id=eq.${encodeURIComponent(categoryId)}&is_archived=eq.true`, // Extra safety check
      null, // No fallback
      `Delete archived survey category: ${categoryId}`
    );

    console.log('Category deletion result:', { categoryDeleteResult, count: categoryDeleteResult?.length || 0 });

    return { data: categoryDeleteResult, error: null };
  } catch (err) {
    console.error('Error deleting archived survey category:', err);
    return { data: null, error: err };
  }
};